import { TopicLayerSettings } from '@agissc/ktag-map';
import { IAppStore } from '../IAppStore';
import { FlyinNames } from '../UIStateSlice';
import { SubLayerProps } from '../tools/mapContent/api/Contracts';
import { SearchProps } from '../tools/mapContent/store/AttributeSearchSlice';
import { ToolNames } from '../tools/ToolNames';
import { MapContentViews } from '../tools/MapContentViews';
import config from './AppConfig';

const parseIdRanges = (input: string): number[] => {
  const ids: number[] = [];

  const parts = input.split(',');

  parts.forEach((part) => {
    const range = part.split('-').map(Number);

    if (range.length === 1) {
      ids.push(range[0]);
    } else {
      for (let i = range[0]; i <= range[1]; i += 1) {
        ids.push(i);
      }
    }
  });

  return ids;
};

const parseNumberParam = (param: string | null): number | undefined =>
  !param || Number.isNaN(+param) ? undefined : Number(param);

const parseBasemap = (basemap: string | null) => {
  if (!basemap) return null;

  const params = basemap.split(',');
  const [basemapId, opacity = 1, visible = 'true'] = params;

  return {
    selectedBasemapId: basemapId,
    isBasemapVisible: visible !== 'false',
    basemapOpacity: +opacity,
  };
};

const parseLocationExtent = (
  xmin: number | undefined,
  xmax: number | undefined,
  ymin: number | undefined,
  ymax: number | undefined,
) => {
  if (!xmin || !xmax || !ymin || !ymax) return null;

  return {
    location: {
      extent: {
        xMin: xmin,
        xMax: xmax,
        yMin: ymin,
        yMax: ymax,
      },
    },
  };
};

const parseLocationCenter = (center: string | null, zoom: number | undefined) => {
  if (!center) return null;

  const coords = center.split(',');
  const x = parseNumberParam(coords[0]);
  const y = parseNumberParam(coords[1]);

  if (!x || !y) return null;

  return {
    location: {
      center: {
        x,
        y,
      },
      zoom: zoom ?? 3,
    },
  };
};

const parseLayers = (layers: string | null) => {
  if (!layers) return undefined;

  const selectedTopicLayers: string[] = [];
  const topicLayerSettings: Record<string, TopicLayerSettings> = {};
  const topicSubLayerSetting: Record<string, SubLayerProps[]> = {};

  layers.split('|').forEach((layer) => {
    const [layerId, opacityRaw, isVisibleRaw, idsRaw] = layer.split(';');

    const opacityValue = parseFloat(opacityRaw);
    const opacity = Number.isNaN(opacityValue) ? 1 : opacityValue;
    const isVisible = isVisibleRaw ? isVisibleRaw?.toLowerCase() === 'true' : true;

    selectedTopicLayers.push(layerId);
    topicLayerSettings[layerId] = {
      opacity,
      isVisible,
    };

    const ids = idsRaw?.trim() || null;

    if (ids) {
      const enabledIds = parseIdRanges(ids);
      topicSubLayerSetting[layerId] = enabledIds.map(
        (id) =>
          ({
            id,
            isEnabled: true,
            isExpanded: false,
          }) as SubLayerProps,
      );
    }
  });

  return { selectedTopicLayers, topicLayerSettings, topicSubLayerSetting };
};

const parseIdentify = (info: string | null) => {
  if (!info) return null;

  const params = info.split(',');
  const x = parseNumberParam(params[0]);
  const y = parseNumberParam(params[1]);
  const tolerance = parseNumberParam(params[2]);

  if (!x || !y) return null;

  return { activeFlyin: FlyinNames.Identify, identifyCoordinate: [x, y], tolerance };
};

const parseSearch = (search: string | null) => {
  if (!search) return null;

  const [detail, origin] = search.split(',');

  if (!detail) return null;

  return {
    searchText: detail,
    selectedSearchResult: { detail, origin },
  };
};

const parseAttributeQuery = (queryString: string | null) => {
  if (!queryString) return null;

  const params = queryString.split(',');

  if (params.length < 4) return null;

  // legacy format is: topiclayer,sublayerId,attribute,query
  // current format is: topiclayer,sublayerId,attribute,searchText,query,viewVisible
  const isLegacyFormat = params.length === 4;
  const topicLayer = params[0];
  const sublayerId = Number(params[1]);
  const attribute = params[2];
  const searchText = isLegacyFormat ? '' : params[3];
  const query = isLegacyFormat ? params[3] : params[4];
  const viewVisible = isLegacyFormat || params[5] === 'true';

  const attributeSearch: Record<string, SearchProps> = {
    [topicLayer]: {
      sublayerId,
      attribute,
      searchText,
      draftQuery: query,
      query,
    },
  };
  const currentAttributeSearchTopicId = topicLayer;

  return { attributeSearch, currentAttributeSearchTopicId, viewVisible };
};

const forceAuth = async (target: string): Promise<boolean> => {
  const response = await fetch(config.apis.auth, { credentials: 'include' });

  if (!response.ok) {
    window.location.href = `${config.apis.login}?target=${target}`;
    return false;
  }

  return true;
};

const getUrlParams = async () => {
  const searchParams = new URLSearchParams(window.location.search);

  const hasAnyParam = !searchParams.keys().next().done;
  if (!hasAnyParam) return null;

  if (searchParams.has('forceauth')) {
    searchParams.delete('forceauth');

    const targetUrl = new URL(window.location.href);
    targetUrl.search = searchParams.toString();
    const target = encodeURIComponent(targetUrl.toString());

    const isAuthenticated = await forceAuth(target);
    if (!isAuthenticated) return null;
  }

  const basemap = parseBasemap(searchParams.get('basemap'));

  const centerLocation = parseLocationCenter(
    searchParams.get('center'),
    parseNumberParam(searchParams.get('z')),
  );

  const extentLocation = parseLocationExtent(
    parseNumberParam(searchParams.get('xmin')),
    parseNumberParam(searchParams.get('xmax')),
    parseNumberParam(searchParams.get('ymin')),
    parseNumberParam(searchParams.get('ymax')),
  );

  const location = centerLocation ?? extentLocation;
  const layers = parseLayers(searchParams.get('layers'));

  const legend = searchParams.has('legend');
  const identify = parseIdentify(searchParams.get('info'));

  const flyin = legend ? { activeFlyin: FlyinNames.Legends } : identify;
  const search = parseSearch(searchParams.get('search'));

  const { attributeSearch, currentAttributeSearchTopicId, viewVisible } =
    parseAttributeQuery(searchParams.get('query')) || {};
  const queryTool =
    viewVisible && attributeSearch
      ? {
          activeTool: ToolNames.layerSelection,
          activeView: MapContentViews.topicAttributeSearch,
          currentTopicLayerId: Object.keys(attributeSearch)[0],
        }
      : null;

  const attributeQuery = { attributeSearch, currentAttributeSearchTopicId };

  const ui = { displayStartUpMessage: searchParams.has('welcome') };

  return {
    ...basemap,
    ...location,
    ...layers,
    ...flyin,
    ...identify,
    ...search,
    ...attributeQuery,
    ...queryTool,
    ...ui,
  } as Partial<IAppStore>;
};

export default getUrlParams;
